import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { Nullable } from 'global';
import { MutationResponse } from 'global';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import { quoteAPI } from '../api';
import { Quote, QuoteFormValues } from '../types';
import { createOrUpdateQuoteDTOSchema } from '../model/schema';
import {
  CreateOrUpdateQuoteVariables,
  GetQuoteByIdVariables,
  GetQuoteByIdResponse,
  GetQuoteResponse,
  GetQuoteVariables,
  QuoteAPINameSpase,
} from '../api/types';

export function useQuote(voId?: string) {
  const {
    error,
    data: quoteResponse,
    loading: isQuoteLoading,
  } = useQuery<GetQuoteResponse, GetQuoteVariables>(quoteAPI.getQuote, {
    skip: !voId,
    notifyOnNetworkStatusChange: true,
    variables: { vendorOpportunityId: voId! },
  });

  const [onUpdate, { loading }] = useMutation<QuoteAPINameSpase<MutationResponse<Quote>>, CreateOrUpdateQuoteVariables>(
    quoteAPI.upsertQuote
  );

  const [fetchQuoteById] = useLazyQuery<GetQuoteByIdResponse, GetQuoteByIdVariables>(quoteAPI.getQuoteById, {
    fetchPolicy: 'network-only',
  });

  const handleFetchQuoteById = useCallback(
    async (quoteId: string): Promise<Nullable<Quote>> => {
      try {
        const { data } = await fetchQuoteById({ variables: { quoteId } });
        return data?.quote?.quoteById?.entity ?? null;
      } catch (error) {
        logError(error);
        return null;
      }
    },
    [fetchQuoteById]
  );

  const handleCreateOrUpdateQuote = useCallback(
    async (voId: string, updateDTO: QuoteFormValues) => {
      try {
        const collectDTO: CreateOrUpdateQuoteVariables['input'] = { vendorOpportunityId: voId, ...updateDTO };

        const { success, error, data } = createOrUpdateQuoteDTOSchema.safeParse(collectDTO);

        if (success) {
          await onUpdate({ variables: { input: data } });
        } else {
          logError('handleUpdateQuote input parse failed', error);
        }
      } catch (error) {
        logError(error);
      }
    },
    [onUpdate]
  );

  return {
    error,
    quoteAPI,
    isQuoteLoading,
    handleFetchQuoteById,
    handleCreateOrUpdateQuote,
    isQuoteCreatingOrUpdating: loading,
    quote: quoteResponse?.vendorOpportunity?.quote ?? null,
  };
}
